// Shared "Connect GitHub" modal for the setup flow (#1274 F4 / #1277).
//
// The assigned Technical Lead (a TENANT_MEMBER) drives ring 1, but the admin
// Settings GitHub control is TENANT_ADMIN-only — so this modal POSTs a PAT to
// the SETUP-SCOPED endpoint `POST /setup-project/connect-github`, which
// authorizes the assigned TL (or self-handling Buyer) via the setup-identity
// gate and fires the existing connect_github completion. Used by BOTH the
// dashboard OnboardingPanel and the /setup hub — one component, no drift
// (mirrors InviteTechnicalLeadModal, #1273). The host owns open/close + refetch
// (onSuccess).

import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';

interface Props {
  /** Fired on a successful connect (HTTP 2xx). Host closes the modal + refetches. */
  onSuccess: () => void;
  /** Fired on Cancel / backdrop dismiss (only while no request is in flight). */
  onClose: () => void;
}

export default function SetupGithubConnectModal({ onSuccess, onClose }: Props) {
  const [token, setToken]           = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const unmounted = useRef(false);
  useEffect(() => {
    unmounted.current = false;
    return () => { unmounted.current = true; };
  }, []);

  // On 2xx hand control back to the host (close + refetch). On 4xx/5xx the
  // server message is surfaced inline and the modal stays open. The endpoint
  // resolves the active setup project from the caller — no id is sent.
  const handleSubmit = (): Promise<void> => {
    setConnecting(true);
    setError(null);
    return apiFetch('/setup-project/connect-github', {
      method: 'POST',
      body: JSON.stringify({ token: token.trim() }),
    })
      .then(() => {
        if (unmounted.current) return;
        onSuccess();
      })
      .catch((err: unknown) => {
        if (unmounted.current) return;
        setError(err instanceof Error ? err.message : 'Could not connect GitHub. Please try again.');
      })
      .finally(() => {
        if (!unmounted.current) setConnecting(false);
      });
  };

  const submitDisabled = connecting || !token.trim();

  return (
    <div
      data-testid="setup-github-connect-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={() => { if (!connecting) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="setup-github-connect-title"
        style={{
          background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
          padding: 24, width: 440, maxHeight: '85vh', overflowY: 'auto',
          boxShadow: 'var(--shadow-lg)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 id="setup-github-connect-title" style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          Connect GitHub
        </h3>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 16px' }}>
          Paste a GitHub Personal Access Token so BluePrint can review code and analyse pull
          requests. It is stored encrypted.
        </p>

        <label htmlFor="setup-github-token" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary, #6b7280)', marginBottom: 4 }}>
          Personal Access Token
        </label>
        <input
          id="setup-github-token"
          data-testid="setup-github-token"
          type="password"
          value={token}
          onChange={e => setToken(e.target.value)}
          placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
          style={{
            width: '100%', padding: '8px 10px', marginBottom: 16,
            border: '1px solid var(--border)', borderRadius: 6,
            fontFamily: 'var(--font-body)', fontSize: 14, boxSizing: 'border-box',
          }}
        />

        {error && (
          <p
            data-testid="setup-github-connect-error"
            style={{ fontSize: 13, color: '#ef4444', marginBottom: 12, marginTop: 0 }}
          >
            {error}
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            data-testid="setup-github-connect-cancel"
            onClick={() => onClose()}
            disabled={connecting}
            style={{
              padding: '8px 14px', borderRadius: 6,
              background: 'transparent', color: 'var(--text-secondary, #6b7280)',
              border: '1px solid var(--border)', cursor: connecting ? 'not-allowed' : 'pointer',
              fontSize: 14, opacity: connecting ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            data-testid="setup-github-connect-submit"
            onClick={() => void handleSubmit()}
            disabled={submitDisabled}
            style={{
              padding: '8px 14px', borderRadius: 6,
              background: '#7c3aed', color: '#fff',
              border: 'none', fontWeight: 500,
              cursor: submitDisabled ? 'not-allowed' : 'pointer',
              fontSize: 14, opacity: submitDisabled ? 0.6 : 1,
            }}
          >
            {connecting ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      </div>
    </div>
  );
}
